import React, { useMemo, useCallback } from 'react';
import {
  arrayOf, bool, exact, func, number, string,
} from 'prop-types';
import styled, { css } from 'styled-components';
import {
  Flex, Typography, ImageBackground, Icon, Button, Circle,
} from '@atoms';
import { Drawer } from '@molecules';
import { MenuItemOptions, MenuItemSizes } from '@organisms';
import { Menu } from '@templates';
import { formatPrice } from '@helpers/menuItemDetails';
import { ReactComponent as Edit } from '@assets/edit.svg';

const MenuItemDetails = ({
  onBackUp,
  itemId,
  onMenuItemProcess,
  onSizeSelect,
  size,
  categories,
  subcategories,
  specialPrice,
  onCategorySelect,
  selectedCategoryId,
  onOptionValueSelect,
  title,
  price,
  imageSrc,
  description,
  isDrawerOpened,
  optionsList,
  onDrawerOpen,
  onOptionQuantityDecrement,
  onOptionQuantityIncrement,
}) => {
  const memoizedOptionsList = useMemo(() => (
    optionsList.map(({
      requiredValue,
      id,
      name,
      maxRequiredValuesAmount,
      allowValueMultipleSelection,
      values,
      valuesSelected,
    }) => (
      <MenuItemOptions
        requiredValue={requiredValue}
        allowValueMultipleSelection={allowValueMultipleSelection}
        onOptionQuantityDecrement={onOptionQuantityDecrement}
        onOptionQuantityIncrement={onOptionQuantityIncrement}
        key={id}
        id={id}
        name={name}
        maxRequiredValuesAmount={maxRequiredValuesAmount}
        values={values}
        valuesSelected={valuesSelected}
        onOptionValueSelect={onOptionValueSelect}
      />
    ))
  ), [optionsList]);

  const getItemPriceComponent = useCallback(() => {
    if (!specialPrice && specialPrice !== 0) {
      return (
        <StyledCircle
          diameter={47}
          grow={0}
          justifyCenter
          itemsCenter
          backgroundColor="darkGrey"
        >
          <Typography color="white" size={(price / 100) % 1 ? 'md' : 'xl2'} weight="bold">
            {`$${formatPrice(price)}`}
          </Typography>
        </StyledCircle>
      );
    }

    return (
      <StyledPriceWrapper row justifyEnd>
        <StyledCircle
          diameter={47}
          grow={0}
          justifyCenter
          itemsCenter
          backgroundColor="strawberry"
        >
          <Typography color="white" size={(price / 100) % 1 ? 'md' : 'xl2'} weight="medium">
            {`$${formatPrice(price)}`}
          </Typography>
        </StyledCircle>
        <StyledCircle
          diameter={47}
          grow={0}
          justifyCenter
          itemsCenter
          backgroundColor="white"
        >
          <Typography color="grannySmith" size={(specialPrice / 100) % 1 ? 'md' : 'xl2'} weight="bold">
            {`$${formatPrice(specialPrice)}`}
          </Typography>
        </StyledCircle>
      </StyledPriceWrapper>
    );
  }, [price, specialPrice]);

  return (
    <Menu
      onBackUp={onBackUp}
      isFooterVisible={!isDrawerOpened}
      hasMarginBottom={false}
      categories={categories}
      subcategories={subcategories}
      onCategorySelect={onCategorySelect}
      selectedCategoryId={selectedCategoryId}
    >
      <Wrapper
        hasSubcategories={Boolean(subcategories.length)}
        column
        justifyEnd
        imageSrc={imageSrc}
      >
        <Flex itemsEnd column padding="xl5">
          <Button.Circular
            onClick={onMenuItemProcess(itemId)}
            secondary
            diameter={43}
            grow={0}
            justifyCenter
            itemsCenter
            backgroundColor="grannySmith"
          >
            <Flex itemsCenter justifyCenter>
              <Icon SVG={Edit} width="26px" height="26px" />
            </Flex>
          </Button.Circular>
        </Flex>
        <Flex grow={0} column padding="xl5">
          <Flex
            justifyBetween
            itemsCenter
            marginBottom={isDrawerOpened ? 'none' : 'lg'}
          >
            <StyledTypography color="white" size="xl4" weight="black">
              {title.toUpperCase()}
            </StyledTypography>
            {getItemPriceComponent()}
          </Flex>
          {!isDrawerOpened && (
            <StyledTypography
              color="white"
              size="lg"
              weight="bold"
            >
              {description}
            </StyledTypography>
          )}
        </Flex>
        <Drawer isDrawerOpened={isDrawerOpened} onDrawerOpen={onDrawerOpen}>
          <Options grow={0} isDrawerOpened={isDrawerOpened} column>
            {Boolean(size.sizes.length) && (
              <MenuItemSizes
                sizes={size.sizes}
                sizeSelected={size.sizeSelected}
                onSizeSelect={onSizeSelect}
              />
            )}
            {memoizedOptionsList}
          </Options>
        </Drawer>
      </Wrapper>
    </Menu>
  );
};

const Options = styled(Flex)`
  ${({ isDrawerOpened, theme: { color } }) => css`
    overflow-y: ${isDrawerOpened ? 'scroll' : 'hidden'};

    ::-webkit-scrollbar {
      -webkit-appearance: none;
    }

    ::-webkit-scrollbar:vertical {
      width: 4px;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 9px;
      background-color: ${color.warmGrey};
    }
  `}
`;

const StyledCircle = styled(Circle)`
  ${() => css`
    min-width: 43px;
  `}
`;

const StyledTypography = styled(Typography)`
  ${({ theme: { color } }) => css`
    text-shadow: 1px 1px ${color.black};
  `}
`;

const StyledPriceWrapper = styled(Flex)`
  ${({ theme: { spacing } }) => css`
    div:first-child {
      margin-right: ${spacing.md};

      p {
        text-decoration: line-through;
      }
    }
  `}
`;

const Wrapper = styled(ImageBackground)`
  ${() => css`
    width: 100%;
    height: calc(100vh - 112px);
  `}
`;

MenuItemDetails.defaultProps = {
  imageSrc: '',
  specialPrice: null,
};

MenuItemDetails.propTypes = {
  specialPrice: number,
  onOptionQuantityDecrement: func.isRequired,
  onOptionQuantityIncrement: func.isRequired,
  itemId: string.isRequired,
  onMenuItemProcess: func.isRequired,
  size: exact({
    sizeSelected: number,
    sizes: arrayOf(exact({
      name: string,
      price: number,
      id: number,
    })),
  }).isRequired,
  onSizeSelect: func.isRequired,
  categories: arrayOf(exact({
    title: string,
    id: number,
  })).isRequired,
  subcategories: arrayOf(exact({
    title: string,
    id: number,
  })).isRequired,
  onCategorySelect: func.isRequired,
  selectedCategoryId: number.isRequired,
  onOptionValueSelect: func.isRequired,
  title: string.isRequired,
  price: number.isRequired,
  imageSrc: string,
  description: string.isRequired,
  isDrawerOpened: bool.isRequired,
  optionsList: arrayOf(exact({
    allowValueMultipleSelection: bool,
    name: string,
    requiredValue: bool.isRequired,
    maxRequiredValuesAmount: number,
    id: number,
    valuesSelected: arrayOf(exact({
      id: number,
      quantity: number,
    })),
    values: arrayOf(exact({
      id: number,
      imageSrc: string,
      name: string,
      price: number,
    })),
  })).isRequired,
  onDrawerOpen: func.isRequired,
  onBackUp: func.isRequired,
};

export default MenuItemDetails;
